import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Calendar, DollarSign, FileText, HelpCircle, LogOut,
  CheckCircle, Clock, CreditCard, Mail, Phone, MapPin, Loader2,
  Edit, AlertCircle, Users, Plus, Minus, X
} from "lucide-react";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { PetPolicyDialog } from "@/components/PetPolicyDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GuestPortal() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [showModificationForm, setShowModificationForm] = useState(false);
  const [submittingModification, setSubmittingModification] = useState(false);
  const [availableAddons, setAvailableAddons] = useState([]);
  const [addonAvailability, setAddonAvailability] = useState({});
  const [modificationRequests, setModificationRequests] = useState([]);
  const [guestCharges, setGuestCharges] = useState(null);
  const [showPetPolicy, setShowPetPolicy] = useState(false);
  const [pendingPetCount, setPendingPetCount] = useState(0);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  
  const [modificationData, setModificationData] = useState({
    check_in: "",
    check_out: "",
    guest_count: 0,
    selected_addons: [],
    pet_count: 0,
    pet_policy_agreed: false,
    notes: ""
  });
  
  const [paymentSubmission, setPaymentSubmission] = useState({
    payment_type: "second",
    payment_method: "venmo",
    confirmation_number: "",
    notes: "",
    amount: 0
  });

  const fetchAvailableAddons = async () => {
    try {
      const res = await axios.get(`${API}/addons`);
      setAvailableAddons(res.data);
    } catch (e) {
      console.error("Error fetching addons:", e);
    }
  };
  
  const fetchAddonAvailability = async (checkIn, checkOut) => {
    try {
      const res = await axios.get(`${API}/addons/availability?check_in=${checkIn}&check_out=${checkOut}`);
      setAddonAvailability(res.data);
    } catch (e) {
      console.error("Error fetching addon availability:", e);
    }
  };
  
  const fetchModificationRequests = async (token, bookingId) => {
    try {
      const res = await axios.get(`${API}/guest/modification-requests/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModificationRequests(res.data);
    } catch (e) {
      console.error("Error fetching modification requests:", e);
    }
  };

  const fetchGuestCharges = async (token, bookingId) => {
    try {
      const res = await axios.get(`${API}/guest/booking/${bookingId}/charges`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGuestCharges(res.data);
    } catch (e) {
      console.error("Error fetching charges:", e);
    }
  };

  const fetchBooking = async (token, bookingId) => {
    try {
      const res = await axios.get(`${API}/guest/booking/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBooking(res.data);
      // Initialize modification data with current booking values
      const currentAddons = (res.data.selected_addons || []).filter(a => (a.id || a.type) !== "pet_fee");
      setModificationData({
        check_in: res.data.check_in || "",
        check_out: res.data.check_out || "",
        guest_count: res.data.guest_count || 1,
        selected_addons: currentAddons,
        pet_count: res.data.pet_count || 0,
        pet_policy_agreed: (res.data.pet_count || 0) > 0 ? !!res.data.pet_policy_agreed : false,
        notes: ""
      });
      // Check addon availability for this booking
      if (res.data.check_in && res.data.check_out) {
        fetchAddonAvailability(res.data.check_in, res.data.check_out);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to load booking details");
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchFAQs = async () => {
    try {
      const res = await axios.get(`${API}/guest/faqs`);
      setFaqs(res.data);
    } catch (error) {
      console.error("Error loading FAQs:", error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("guest_token");
    localStorage.removeItem("guest_email");
    localStorage.removeItem("booking_id");
    navigate("/guest-login");
  };

  useEffect(() => {
    const token = localStorage.getItem("guest_token");
    const bookingId = localStorage.getItem("booking_id");

    if (!token || !bookingId) {
      navigate("/guest-login");
      return;
    }

    const timer = setTimeout(() => {
      fetchBooking(token, bookingId);
      fetchFAQs();
      fetchAvailableAddons();
      fetchModificationRequests(token, bookingId);
      fetchGuestCharges(token, bookingId);
    }, 0);
    return () => clearTimeout(timer);
  }, [navigate]);

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setSubmittingPayment(true);

    try {
      const token = localStorage.getItem("guest_token");
      await axios.post(
        `${API}/guest/submit-payment`,
        {
          ...paymentSubmission,
          booking_id: booking.id
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Payment submission received! We'll review it shortly.");
      setShowPaymentForm(false);
      setPaymentSubmission({
        payment_type: "second",
        payment_method: "venmo",
        confirmation_number: "",
        notes: "",
        amount: 0
      });
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to submit payment. Please try again.");
    } finally {
      setSubmittingPayment(false);
    }
  };

  const handleModificationSubmit = async (e) => {
    e.preventDefault();
    setSubmittingModification(true);

    try {
      const token = localStorage.getItem("guest_token");
      const res = await axios.post(
        `${API}/guest/modification-request`,
        {
          booking_id: booking.id,
          check_in: modificationData.check_in,
          check_out: modificationData.check_out,
          guest_count: modificationData.guest_count,
          selected_addons: modificationData.selected_addons,
          pet_count: modificationData.pet_count,
          pet_policy_agreed: modificationData.pet_policy_agreed,
          notes: modificationData.notes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const priceDiff = res.data.price_difference;
      const priceMessage = priceDiff > 0 
        ? `This will add $${priceDiff.toFixed(2)} to your balance.`
        : priceDiff < 0 
          ? `This will reduce your balance by $${Math.abs(priceDiff).toFixed(2)}.`
          : '';
      
      toast.success(`Modification request submitted! ${priceMessage} We'll review it shortly.`);
      setShowModificationForm(false);
      
      // Refresh modification requests
      fetchModificationRequests(token, booking.id);
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.response?.data?.detail || "Failed to submit modification request. Please try again.");
    } finally {
      setSubmittingModification(false);
    }
  };

  const toggleAddon = (addon) => {
    setModificationData(prev => {
      const currentAddons = prev.selected_addons || [];
      const exists = currentAddons.find(a => a.id === addon.id);
      
      if (exists) {
        // Remove addon
        return {
          ...prev,
          selected_addons: currentAddons.filter(a => a.id !== addon.id)
        };
      } else {
        // Add addon
        return {
          ...prev,
          selected_addons: [...currentAddons, {
            id: addon.id,
            name: addon.name,
            price: addon.price,
            type: addon.type,
            quantity: 1,
            total: addon.price
          }]
        };
      }
    });
  };

  const setPets = (n) => {
    if (n > 0 && !modificationData.pet_policy_agreed) {
      setPendingPetCount(n);
      setShowPetPolicy(true);
      return;
    }
    setModificationData(prev => ({
      ...prev,
      pet_count: n,
      pet_policy_agreed: n > 0 ? prev.pet_policy_agreed : false,
    }));
  };

  const handlePetPolicyAccept = () => {
    setModificationData(prev => ({ ...prev, pet_count: pendingPetCount || 1, pet_policy_agreed: true }));
    setShowPetPolicy(false);
  };

  // Live price preview from the server (exact figure the admin will see)
  useEffect(() => {
    if (!showModificationForm || !booking) return;
    const token = localStorage.getItem("guest_token");
    const timer = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/guest/modification-preview`, {
          booking_id: booking.id,
          check_in: modificationData.check_in,
          check_out: modificationData.check_out,
          guest_count: modificationData.guest_count,
          selected_addons: modificationData.selected_addons,
          pet_count: modificationData.pet_count,
          pet_policy_agreed: modificationData.pet_policy_agreed,
        }, { headers: { Authorization: `Bearer ${token}` } });
        setPreview(res.data);
        setPreviewError("");
      } catch (err) {
        setPreview(null);
        setPreviewError(err.response?.data?.detail || "");
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [showModificationForm, booking, modificationData]);

  const canModify = () => {
    if (!booking) return false;
    const checkInDate = parseISO(booking.check_in);
    const today = new Date();
    const daysUntil = Math.floor((checkInDate - today) / (1000 * 60 * 60 * 24));
    return daysUntil >= 3;
  };

  const hasPendingModification = () => {
    return modificationRequests.some(r => r.status === 'pending');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#134E4A]" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <p>Booking not found</p>
      </div>
    );
  }

  const buildPaymentSchedule = (b) => {
    // Newer bookings may already store an array; older/standard bookings store a string + individual fields
    if (Array.isArray(b.payment_schedule)) return b.payment_schedule;
    const sched = [];
    const depositAmount = b.first_payment ?? b.deposit_amount ?? 0;
    const depositPaid = b.deposit_paid || (b.amount_paid ?? 0) >= depositAmount && depositAmount > 0;
    sched.push({
      payment: "Deposit",
      amount: depositAmount,
      method: b.payment_method === "stripe" ? "Credit Card" : (b.payment_method || "Credit Card"),
      status: depositPaid ? "paid" : "pending",
    });
    if ((b.second_payment ?? 0) > 0) {
      sched.push({
        payment: "Second Payment",
        amount: b.second_payment,
        due_date: b.second_payment_due,
        status: b.second_payment_paid ? "paid" : "pending",
      });
    }
    if ((b.third_payment ?? 0) > 0) {
      sched.push({
        payment: "Final Payment",
        amount: b.third_payment,
        due_date: b.third_payment_due,
        status: b.third_payment_paid ? "paid" : "pending",
      });
    }
    if ((b.protection_amount ?? 0) > 0 && b.protection_type === "deposit") {
      sched.push({
        payment: "Security Deposit (HOLD only)",
        amount: b.protection_amount,
        method: "Card hold",
        status: b.deposit_status === "held" ? "paid" : "pending",
      });
    }
    return sched;
  };

  const paymentSchedule = buildPaymentSchedule(booking);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-serif text-[#134E4A]">Chestnut Hill Inn</h1>
            <p className="text-sm text-[#78716C]">Guest Portal</p>
          </div>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="flex items-center gap-2"
          >
            <LogOut className="h-4 w-4" />
            Logout
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-2xl font-serif text-[#134E4A] mb-2">
            Welcome, {booking.guest_name}!
          </h2>
          <p className="text-[#78716C]">
            Confirmation #: <span className="font-medium text-[#134E4A]">{booking.id.slice(0, 8).toUpperCase()}</span>
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Reservation Details */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="h-5 w-5 text-[#134E4A]" />
              <h3 className="text-xl font-serif text-[#134E4A]">Reservation Details</h3>
            </div>
            
            <div className="space-y-3">
              <div>
                <p className="text-sm text-[#78716C]">Check-in</p>
                <p className="font-medium text-[#134E4A]">
                  {format(parseISO(booking.check_in), "MMMM d, yyyy")} at {booking.check_in_time || "4:00 PM"}
                </p>
              </div>
              
              <div>
                <p className="text-sm text-[#78716C]">Check-out</p>
                <p className="font-medium text-[#134E4A]">
                  {format(parseISO(booking.check_out), "MMMM d, yyyy")} at {booking.check_out_time || "10:00 AM"}
                </p>
              </div>
              
              <div>
                <p className="text-sm text-[#78716C]">Guests</p>
                <p className="font-medium text-[#134E4A]">{booking.guest_count} guests</p>
              </div>
              
              <div>
                <p className="text-sm text-[#78716C]">Total Nights</p>
                <p className="font-medium text-[#134E4A]">{booking.total_nights || booking.nights || 'N/A'} nights</p>
              </div>

              {booking.selected_addons && booking.selected_addons.length > 0 && (
                <div>
                  <p className="text-sm text-[#78716C]">Add-ons</p>
                  {booking.selected_addons.map((addon, idx) => (
                    <p key={idx} className="font-medium text-[#134E4A]">• {addon.name}</p>
                  ))}
                </div>
              )}
            </div>
            
            {/* Request Modification Button */}
            <div className="mt-6 pt-4 border-t">
              {!canModify() ? (
                <p className="text-sm text-amber-600 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Modifications cannot be made within 3 days of check-in
                </p>
              ) : hasPendingModification() ? (
                <p className="text-sm text-amber-600 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  You have a pending modification request under review
                </p>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => setShowModificationForm(!showModificationForm)}
                  className="w-full flex items-center justify-center gap-2"
                >
                  <Edit className="h-4 w-4" />
                  Request Modification
                </Button>
              )}
            </div>
          </div>

          {/* Payment Schedule */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-[#134E4A]" />
                <h3 className="text-xl font-serif text-[#134E4A]">Payment Schedule</h3>
              </div>
              <Button
                onClick={() => setShowPaymentForm(!showPaymentForm)}
                className="bg-[#134E4A] hover:bg-[#0F3A38]"
                size="sm"
              >
                Submit Payment
              </Button>
            </div>

            <div className="space-y-3">
              {paymentSchedule.map((payment, idx) => (
                <div key={idx} className="flex justify-between items-center p-3 bg-[#F8F9FA] rounded">
                  <div>
                    <p className="font-medium text-[#134E4A]">{payment.payment}</p>
                    <p className="text-sm text-[#78716C]">
                      {payment.due_date ? `Due: ${payment.due_date}` : payment.method}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-[#134E4A]">${(payment.amount || 0).toFixed(2)}</p>
                    {payment.status === "paid" ? (
                      <div className="flex items-center gap-1 text-green-600 text-sm">
                        <CheckCircle className="h-4 w-4" />
                        Paid
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-orange-600 text-sm">
                        <Clock className="h-4 w-4" />
                        Pending
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t">
              <div className="flex justify-between items-center">
                <p className="font-semibold text-[#134E4A]">Total Trip Cost</p>
                <p className="font-semibold text-[#134E4A] text-xl">
                  ${(booking.total_amount || booking.total_price || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Extra Charges & Deposit Hold */}
        {guestCharges && ((guestCharges.extra_charges?.length > 0) || (guestCharges.deposit_holds?.length > 0)) && (
          <div className="mt-6 bg-white rounded-lg shadow p-6" data-testid="guest-charges-section">
            <h3 className="text-xl font-serif text-[#134E4A] mb-4">Additional Charges & Security Deposit</h3>
            <div className="space-y-3">
              {(guestCharges.extra_charges || []).map((c) => (
                <div key={c.id} className="flex justify-between items-center p-3 bg-[#F8F9FA] rounded" data-testid={`guest-extra-charge-${c.id}`}>
                  <div>
                    <p className="font-medium text-[#134E4A]">{c.reason}</p>
                    <p className="text-sm text-[#78716C]">${(c.amount || 0).toFixed(2)}</p>
                  </div>
                  {c.status === "paid" ? (
                    <div className="flex items-center gap-1 text-green-600 text-sm">
                      <CheckCircle className="h-4 w-4" /> Paid
                    </div>
                  ) : (
                    <Button size="sm" className="bg-[#134E4A] hover:bg-[#0F3A38]"
                      onClick={() => window.location.href = `/pay/extra/${c.id}`}
                      data-testid={`guest-pay-charge-${c.id}`}>
                      Pay Now
                    </Button>
                  )}
                </div>
              ))}
              {(guestCharges.deposit_holds || []).map((h) => (
                <div key={h.id} className="flex justify-between items-center p-3 bg-[#F8F9FA] rounded" data-testid={`guest-deposit-hold-${h.id}`}>
                  <div>
                    <p className="font-medium text-[#134E4A]">Security Deposit Hold</p>
                    <p className="text-sm text-[#78716C]">${(h.amount || 0).toFixed(2)} — hold only, not a charge</p>
                  </div>
                  {h.status === "requested" ? (
                    <Button size="sm" className="bg-[#134E4A] hover:bg-[#0F3A38]"
                      onClick={() => window.location.href = `/pay/deposit/${h.id}`}
                      data-testid={`guest-place-hold-${h.id}`}>
                      Place Hold
                    </Button>
                  ) : (
                    <span className="text-sm capitalize text-[#78716C]">{h.status}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Payment Submission Form */}
        {showPaymentForm && (
          <div className="mt-6 bg-white rounded-lg shadow p-6">
            <h3 className="text-xl font-serif text-[#134E4A] mb-4">Submit Manual Payment</h3>
            <form onSubmit={handlePaymentSubmit} className="space-y-4">
              <div>
                <Label>Payment Type</Label>
                <RadioGroup
                  value={paymentSubmission.payment_type}
                  onValueChange={(value) => setPaymentSubmission({ ...paymentSubmission, payment_type: value })}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="deposit" id="deposit" />
                    <Label htmlFor="deposit">Deposit</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="second" id="second" />
                    <Label htmlFor="second">Second Payment</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="final" id="final" />
                    <Label htmlFor="final">Final Payment</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="security" id="security" />
                    <Label htmlFor="security">Security Deposit</Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label>Payment Method</Label>
                <RadioGroup
                  value={paymentSubmission.payment_method}
                  onValueChange={(value) => setPaymentSubmission({ ...paymentSubmission, payment_method: value })}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="venmo" id="venmo" />
                    <Label htmlFor="venmo">Venmo</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="zelle" id="zelle" />
                    <Label htmlFor="zelle">Zelle</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="cashapp" id="cashapp" />
                    <Label htmlFor="cashapp">Cash App</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="check" id="check" />
                    <Label htmlFor="check">Check</Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label htmlFor="amount">Amount Paid</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  value={paymentSubmission.amount}
                  onChange={(e) => setPaymentSubmission({ ...paymentSubmission, amount: parseFloat(e.target.value) })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="confirmation">Confirmation Number (optional)</Label>
                <Input
                  id="confirmation"
                  value={paymentSubmission.confirmation_number}
                  onChange={(e) => setPaymentSubmission({ ...paymentSubmission, confirmation_number: e.target.value })}
                  placeholder="Transaction ID, Check number, etc."
                />
              </div>

              <div>
                <Label htmlFor="notes">Additional Notes (optional)</Label>
                <Textarea
                  id="notes"
                  value={paymentSubmission.notes}
                  onChange={(e) => setPaymentSubmission({ ...paymentSubmission, notes: e.target.value })}
                  placeholder="Any additional information..."
                  rows={3}
                />
              </div>

              <div className="flex gap-2">
                <Button
                  type="submit"
                  disabled={submittingPayment}
                  className="bg-[#134E4A] hover:bg-[#0F3A38]"
                >
                  {submittingPayment ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Submit Payment"
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowPaymentForm(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Modification Request Form */}
        {showModificationForm && (
          <div className="mt-6 bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-serif text-[#134E4A]">Request Modification</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowModificationForm(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-amber-800">
                <strong>Please ensure your guest count matches your contract.</strong> Modifications require admin approval and may affect your booking total.
              </p>
            </div>
            
            <form onSubmit={handleModificationSubmit} className="space-y-6">
              {/* Dates */}
              <div>
                <Label className="text-base font-medium">Dates</Label>
                <p className="text-sm text-[#78716C] mb-3">Request different check-in / check-out dates</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-[#78716C] mb-1 block">Check-in</Label>
                    <Input type="date" value={modificationData.check_in}
                      onChange={(e) => setModificationData(prev => ({ ...prev, check_in: e.target.value }))}
                      data-testid="mod-check-in" />
                  </div>
                  <div>
                    <Label className="text-xs text-[#78716C] mb-1 block">Check-out</Label>
                    <Input type="date" value={modificationData.check_out}
                      onChange={(e) => setModificationData(prev => ({ ...prev, check_out: e.target.value }))}
                      data-testid="mod-check-out" />
                  </div>
                </div>
              </div>

              {/* Guest Count */}
              <div>
                <Label className="text-base font-medium">Number of Guests</Label>
                <p className="text-sm text-[#78716C] mb-2">Current: {booking.guest_count} guests (Maximum: 20)</p>
                <p className="text-xs text-[#78716C] mb-3">14 guests included • Extra guests: $20/night each (up to 6 extras)</p>
                <div className="flex items-center gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setModificationData(prev => ({
                      ...prev,
                      guest_count: Math.max(1, prev.guest_count - 1)
                    }))}
                    disabled={modificationData.guest_count <= 1}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="text-2xl font-medium text-[#134E4A] w-12 text-center" data-testid="mod-guest-count">
                    {modificationData.guest_count}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setModificationData(prev => ({
                      ...prev,
                      guest_count: Math.min(20, prev.guest_count + 1)
                    }))}
                    disabled={modificationData.guest_count >= 20}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Pets */}
              <div>
                <Label className="text-base font-medium">Pets</Label>
                <p className="text-sm text-[#78716C] mb-3">Bringing a pet? Maximum 2. A flat $150 pet fee applies per stay.</p>
                <div className="flex items-center gap-2">
                  {[0, 1, 2].map(n => (
                    <Button key={n} type="button"
                      variant={modificationData.pet_count === n ? "default" : "outline"}
                      className={modificationData.pet_count === n ? "bg-[#134E4A] hover:bg-[#0f3d3a]" : ""}
                      onClick={() => setPets(n)}
                      data-testid={`mod-pets-${n}`}>
                      {n === 0 ? "No pets" : `${n} pet${n > 1 ? 's' : ''}`}
                    </Button>
                  ))}
                </div>
                {modificationData.pet_count > 0 && modificationData.pet_policy_agreed && (
                  <p className="text-xs text-green-700 mt-2 flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Pet policy accepted</p>
                )}
              </div>

              {/* Add-ons Selection */}
              <div>
                <Label className="text-base font-medium">Add-ons</Label>
                <p className="text-sm text-[#78716C] mb-3">Select or deselect add-ons for your stay</p>
                
                <div className="space-y-3">
                  {availableAddons.filter(a => a.active !== false && a.id !== 'pet_fee').map((addon) => {
                    const isSelected = modificationData.selected_addons?.some(a => a.id === addon.id);
                    const isAvailable = addon.type !== 'early_checkin' && addon.type !== 'late_checkout' 
                      ? true 
                      : addonAvailability[addon.type] !== false;
                    
                    return (
                      <div 
                        key={addon.id}
                        className={`flex items-center justify-between p-4 border rounded-lg ${
                          isSelected ? 'border-[#134E4A] bg-[#F0FDF9]' : 'border-gray-200'
                        } ${!isAvailable ? 'opacity-50' : ''}`}
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox
                            id={addon.id}
                            checked={isSelected}
                            onCheckedChange={() => isAvailable && toggleAddon(addon)}
                            disabled={!isAvailable}
                            data-testid={`mod-addon-${addon.id}`}
                          />
                          <div>
                            <Label htmlFor={addon.id} className="font-medium cursor-pointer">
                              {addon.name}
                            </Label>
                            {addon.description && (
                              <p className="text-sm text-[#78716C]">{addon.description}</p>
                            )}
                            {!isAvailable && (
                              <p className="text-sm text-red-500">Not available for your dates</p>
                            )}
                          </div>
                        </div>
                        <span className="font-medium text-[#134E4A]">${addon.price}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label htmlFor="mod-notes">Notes (Optional)</Label>
                <Textarea
                  id="mod-notes"
                  value={modificationData.notes}
                  onChange={(e) => setModificationData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Any additional information about your request..."
                  rows={3}
                />
              </div>

              {/* Summary of Changes (server-calculated) */}
              <div className="bg-[#F8F9FA] rounded-lg p-4" data-testid="mod-summary">
                <h4 className="font-medium text-[#134E4A] mb-3">Summary of Changes</h4>
                {previewError ? (
                  <p className="text-sm text-red-600" data-testid="mod-preview-error">{previewError}</p>
                ) : preview && preview.changes && preview.changes.length > 0 ? (
                  <div className="space-y-2 text-sm">
                    {preview.changes.map((c, idx) => (
                      <div key={idx} className="flex justify-between">
                        <span className="text-[#78716C]">{c.label}:</span>
                        <span className="font-medium">{c.from} → {c.to}{c.note ? ` · ${c.note}` : ''}</span>
                      </div>
                    ))}
                    <div className="border-t pt-2 mt-2 flex justify-between font-medium">
                      <span>Total Price Adjustment:</span>
                      <span data-testid="mod-price-diff" className={preview.price_difference > 0 ? 'text-amber-600' : preview.price_difference < 0 ? 'text-green-600' : ''}>
                        {preview.price_difference > 0 ? '+' : ''}${preview.price_difference.toFixed(2)}
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-[#78716C]">No changes selected yet.</p>
                )}
              </div>

              <div className="flex gap-2">
                <Button
                  type="submit"
                  disabled={submittingModification}
                  className="bg-[#134E4A] hover:bg-[#0F3A38]"
                >
                  {submittingModification ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Submit Request"
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowModificationForm(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Modification Request History */}
        {modificationRequests.length > 0 && (
          <div className="mt-6 bg-white rounded-lg shadow p-6">
            <h3 className="text-xl font-serif text-[#134E4A] mb-4">Modification Request History</h3>
            <div className="space-y-3">
              {modificationRequests.map((req) => (
                <div key={req.id} className="p-4 bg-[#F8F9FA] rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm text-[#78716C]">
                        Submitted: {format(parseISO(req.submitted_at), "MMM d, yyyy 'at' h:mm a")}
                      </p>
                      <p className="font-medium mt-1">
                        Guest count: {req.current_guest_count} → {req.requested_guest_count}
                      </p>
                      <p className="text-sm text-[#78716C]">
                        Add-ons: {req.current_addons?.length || 0} → {req.requested_addons?.length || 0} items
                      </p>
                      <p className="text-sm font-medium">
                        Price adjustment: ${req.price_difference > 0 ? '+' : ''}{req.price_difference?.toFixed(2)}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      req.status === 'approved' 
                        ? 'bg-green-100 text-green-800'
                        : req.status === 'rejected'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-amber-100 text-amber-800'
                    }`}>
                      {req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                    </span>
                  </div>
                  {req.rejection_reason && (
                    <p className="mt-2 text-sm text-red-600">Reason: {req.rejection_reason}</p>
                  )}
                  {req.admin_notes && (
                    <p className="mt-2 text-sm text-[#78716C]">Note: {req.admin_notes}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contact & FAQ Section */}
        <div className="grid md:grid-cols-2 gap-6 mt-6">
          {/* Contact Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-2 mb-4">
              <Mail className="h-5 w-5 text-[#134E4A]" />
              <h3 className="text-xl font-serif text-[#134E4A]">Contact Us</h3>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-[#78716C]" />
                <span className="text-[#134E4A]">booking@chestnuthillinn.com</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-[#78716C]" />
                <span className="text-[#134E4A]">(603) 858-2272</span>
              </div>
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-[#78716C] mt-1" />
                <span className="text-[#134E4A]">Chestnut Hill Inn<br />Long Island, Maine</span>
              </div>
              <a
                href="/rental-agreement"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="portal-rental-agreement-link"
                className="inline-block text-sm text-[#134E4A] underline hover:text-[#0f3d3a] pt-1"
              >
                View Rental Agreement
              </a>
            </div>
          </div>

          {/* FAQs */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-2 mb-4">
              <HelpCircle className="h-5 w-5 text-[#134E4A]" />
              <h3 className="text-xl font-serif text-[#134E4A]">Frequently Asked Questions</h3>
            </div>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {faqs.map((faq, idx) => (
                <div key={idx}>
                  <p className="font-medium text-[#134E4A] text-sm">{faq.question}</p>
                  <p className="text-[#78716C] text-sm mt-1">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <PetPolicyDialog
        open={showPetPolicy}
        onOpenChange={setShowPetPolicy}
        onAccept={handlePetPolicyAccept}
      />
    </div>
  );
}
